import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, LogOut, Gift, Download, Trash2, Mail, BarChart3, BookOpen } from "lucide-react";
import { API_URL, BRAND, resolveAsset } from "../lib/brand";

const STATUS_META = {
  pending: { label: "In attesa", color: "#F59E0B" },
  paid: { label: "Pagato", color: "#10B981" },
  redeemed: { label: "Riscattato", color: "#4E9EA5" },
  cancelled: { label: "Annullato", color: "#9CA3AF" },
};

const OCCASION_LABELS = {
  compleanno: "🎂 Compleanno",
  anniversario: "💗 Anniversario",
  natale: "🎄 Natale",
  "festa-mamma": "👩 Festa della Mamma",
  "festa-papa": "👨 Festa del Papà",
  battesimo: "👶 Battesimo",
  generico: "✨ Speciale",
};

export default function AdminVouchers() {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ total: 0, revenue_paid: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [occasionFilter, setOccasionFilter] = useState("all");
  const navigate = useNavigate();
  const token = localStorage.getItem("tv_admin_token");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token) { navigate("/admin/login"); return; }
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/admin/vouchers`, auth);
      setItems(r.data.items || []);
      setTotals({ total: r.data.total, revenue_paid: r.data.revenue_paid });
    } catch (err) {
      if (err?.response?.status === 401) { localStorage.removeItem("tv_admin_token"); navigate("/admin/login"); }
    } finally { setLoading(false); }
  };

  const emailPaymentDetails = (v) => {
    const subject = `Le coordinate per il tuo voucher · ${v.code}`;
    const amountLine = v.amount > 0 ? `€ ${v.amount.toFixed(2).replace(".00", "")}` : "[importo da definire]";
    const body = `Ciao ${v.buyer_name},

grazie per aver scelto di regalare cura a ${v.recipient_name}. 💗

Ti mando le coordinate per completare l'acquisto del voucher ${v.code}:

Servizio: ${v.service_label}
Importo: ${amountLine}

Coordinate bonifico:
Intestatario: Teresa Vilardo
IBAN: [inserisci qui il tuo IBAN]
Causale: Voucher ${v.code}

Oppure PayPal / Satispay al numero 340 399 3522.

Appena ricevo il pagamento ti mando conferma e il voucher torna a te (o direttamente a ${v.recipient_name} se vuoi).

Con cura,
Teresa`;
    window.open(`mailto:${v.buyer_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, "_blank");
  };

  const printPhysicalCard = (v) => {
    window.open(`${API_URL}/vouchers/${v.id}/card-pdf`, "_blank");
  };

  const changeStatus = async (v, new_status) => {
    try {
      await axios.put(`${API_URL}/admin/vouchers/${v.id}/status?new_status=${new_status}`, {}, auth);
      toast.success(`Stato aggiornato: ${STATUS_META[new_status].label}`);
      load();
    } catch { toast.error("Errore aggiornamento"); }
  };

  const del = async (v) => {
    if (!window.confirm(`Eliminare il voucher ${v.code}?`)) return;
    try {
      await axios.delete(`${API_URL}/admin/vouchers/${v.id}`, auth);
      toast.success("Voucher eliminato");
      load();
    } catch { toast.error("Errore eliminazione"); }
  };

  const logout = () => { localStorage.removeItem("tv_admin_token"); navigate("/admin/login"); };

  const filtered = items
    .filter((v) => filter === "all" || v.status === filter)
    .filter((v) => occasionFilter === "all" || (v.occasion || "generico") === occasionFilter);
  const counts = {
    all: items.length,
    pending: items.filter((v) => v.status === "pending").length,
    paid: items.filter((v) => v.status === "paid").length,
    redeemed: items.filter((v) => v.status === "redeemed").length,
    cancelled: items.filter((v) => v.status === "cancelled").length,
  };
  const pipelineValue = items.filter((v) => v.status === "pending").reduce((s, v) => s + (v.amount || 0), 0);

  return (
    <div className="min-h-screen bg-[#FAF8F5] p-6 md:p-10" data-testid="admin-vouchers-page">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <img src={BRAND.logo} alt="" className="h-12 w-12" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768]">— Regala Cura</p>
              <h1 className="font-display text-3xl md:text-4xl text-[#3D2140]">Voucher</h1>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/admin" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#3D2140]/15 text-[#3D2140] text-sm font-semibold hover:border-[#FF2768]" data-testid="link-dashboard"><ArrowLeft size={14} /> Dashboard</Link>
            <Link to="/admin/newsletter" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#3D2140]/15 text-[#3D2140] text-sm font-semibold hover:border-[#FF2768]"><Mail size={14} /> Newsletter</Link>
            <Link to="/admin/magazine" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#3D2140]/15 text-[#3D2140] text-sm font-semibold hover:border-[#FF2768]"><BookOpen size={14} /> Magazine</Link>
            <button onClick={logout} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#3D2140]/15 text-[#3D2140] text-sm font-semibold hover:border-[#FF2768]"><LogOut size={14} /> Esci</button>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-2xl p-5 border border-[#3D2140]/8">
            <p className="text-[10px] uppercase tracking-widest text-[#3D2140]/50">Totale voucher</p>
            <p className="mt-2 font-display italic text-3xl text-[#3D2140]">{counts.all}</p>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-[#F59E0B]/40">
            <p className="text-[10px] uppercase tracking-widest text-[#F59E0B]">In attesa pagamento</p>
            <p className="mt-2 font-display italic text-3xl text-[#3D2140]">{counts.pending}</p>
            <p className="text-xs text-[#3D2140]/50 mt-1">Da incassare: € {pipelineValue.toFixed(0)}</p>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-[#10B981]/40">
            <p className="text-[10px] uppercase tracking-widest text-[#10B981]">Incassato</p>
            <p className="mt-2 font-display italic text-3xl text-[#3D2140]">€ {totals.revenue_paid.toFixed(0)}</p>
            <p className="text-xs text-[#3D2140]/50 mt-1">{counts.paid + counts.redeemed} voucher</p>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-[#4E9EA5]/40">
            <p className="text-[10px] uppercase tracking-widest text-[#4E9EA5]">Riscattati</p>
            <p className="mt-2 font-display italic text-3xl text-[#3D2140]">{counts.redeemed}</p>
          </div>
        </div>

        {/* Filters — status */}
        <div className="flex flex-wrap gap-2 mb-3">
          {[
            { k: "all", label: "Tutti", c: counts.all },
            { k: "pending", label: "In attesa", c: counts.pending },
            { k: "paid", label: "Pagati", c: counts.paid },
            { k: "redeemed", label: "Riscattati", c: counts.redeemed },
            { k: "cancelled", label: "Annullati", c: counts.cancelled },
          ].map((f) => (
            <button key={f.k} onClick={() => setFilter(f.k)} className={`px-4 py-2 rounded-full text-xs font-semibold ${filter === f.k ? "bg-[#FF2768] text-white" : "bg-white border border-[#3D2140]/10 text-[#3D2140] hover:border-[#FF2768]"}`} data-testid={`filter-${f.k}`}>
              {f.label} <span className="opacity-60">({f.c})</span>
            </button>
          ))}
        </div>

        {/* Filters — occasione */}
        <div className="flex flex-wrap gap-2 mb-5">
          <button onClick={() => setOccasionFilter("all")} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${occasionFilter === "all" ? "bg-[#4E9EA5] text-white" : "bg-white border border-[#3D2140]/10 text-[#3D2140]/70 hover:border-[#4E9EA5]"}`} data-testid="occ-filter-all">
            Ogni occasione
          </button>
          {Object.entries(OCCASION_LABELS).map(([k, label]) => (
            <button key={k} onClick={() => setOccasionFilter(k)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${occasionFilter === k ? "bg-[#4E9EA5] text-white" : "bg-white border border-[#3D2140]/10 text-[#3D2140]/70 hover:border-[#4E9EA5]"}`} data-testid={`occ-filter-${k}`}>
              {label}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="bg-white rounded-2xl border border-[#3D2140]/8 overflow-hidden">
          {loading && <p className="p-10 text-center font-editorial italic text-[#3D2140]/60">Sto caricando...</p>}
          {!loading && filtered.length === 0 && (
            <div className="p-14 text-center">
              <Gift size={40} className="mx-auto text-[#FF2768]/40 mb-3" />
              <p className="font-editorial italic text-xl text-[#3D2140]/60">Nessun voucher in questo stato.</p>
              <p className="mt-2 text-sm text-[#3D2140]/40">Le richieste arriveranno qui appena qualcuno userà la pagina <Link to="/regala-cura" className="text-[#FF2768] underline">/regala-cura</Link></p>
            </div>
          )}
          {filtered.map((v) => {
            const meta = STATUS_META[v.status] || STATUS_META.pending;
            return (
              <div key={v.id} className="p-5 border-b border-[#3D2140]/5 last:border-0 hover:bg-[#FAF8F5]/50" data-testid={`voucher-row-${v.code}`}>
                <div className="flex flex-wrap items-start gap-4">
                  <div className="flex-1 min-w-[280px]">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className="inline-block px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-bold text-white" style={{ background: meta.color }}>
                        {meta.label}
                      </span>
                      <code className="text-xs bg-[#FFE45C] px-2 py-0.5 rounded font-mono font-bold">{v.code}</code>
                      <span className="text-[10px] text-[#3D2140]/50">{new Date(v.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}</span>
                    </div>
                    <p className="font-display text-lg text-[#3D2140] leading-tight">
                      <span className="text-[#FF2768]">{v.buyer_name}</span> → <span className="text-[#4E9EA5]">{v.recipient_name}</span>
                    </p>
                    <p className="text-sm text-[#3D2140]/65 mt-1">
                      {v.service_label}
                      {v.amount > 0 ? <> · <strong className="text-[#FF2768]">€ {v.amount.toFixed(2).replace(".00", "")}</strong></> : <> · <em className="text-[#3D2140]/50">prezzo da comunicare</em></>}
                    </p>
                    {v.occasion && (
                      <p className="text-xs text-[#4E9EA5] mt-1">{OCCASION_LABELS[v.occasion] || `✨ ${v.occasion}`}</p>
                    )}
                    {v.valid_until && (
                      <p className="text-[10px] text-[#3D2140]/50 mt-0.5">Valido fino al {new Date(v.valid_until).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}</p>
                    )}
                    {v.discount_code_used && (
                      <p className="text-xs text-[#10B981] mt-1 font-semibold">🎉 Codice amico usato: {v.discount_code_used} → applica -10%</p>
                    )}
                    {v.friend_code && (
                      <p className="text-xs text-[#4E9EA5] mt-1">💝 Codice amico generato: <code className="bg-[#E8F5F6] px-1.5 py-0.5 rounded font-mono">{v.friend_code}</code> {v.friend_code_used && "(già usato)"}</p>
                    )}
                    <p className="text-xs text-[#3D2140]/50 mt-1">
                      {v.buyer_email}{v.buyer_phone && ` · ${v.buyer_phone}`}
                      {v.payment_preference && ` · Pagamento: ${v.payment_preference}`}
                    </p>
                    {v.personal_message && (
                      <p className="mt-2 text-xs italic text-[#3D2140]/60 bg-[#FAF8F5] p-2 rounded-lg border-l-2 border-[#FF2768]">"{v.personal_message}"</p>
                    )}
                    {v.notes && <p className="mt-2 text-xs text-[#3D2140]/50"><strong>Note:</strong> {v.notes}</p>}
                  </div>

                  <div className="flex flex-wrap gap-2 items-start">
                    {v.pdf_url && (
                      <a href={resolveAsset(v.pdf_url)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#FAF8F5] hover:bg-[#3D2140] hover:text-white text-xs font-semibold" title="Scarica PDF" data-testid={`voucher-pdf-${v.code}`}>
                        <Download size={12} /> PDF
                      </a>
                    )}
                    <button onClick={() => printPhysicalCard(v)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#FAF8F5] hover:bg-[#FFE45C] text-xs font-semibold" title="Card fronte-retro da stampare e piegare" data-testid={`voucher-card-${v.code}`}>
                      🎴 Card
                    </button>
                    {v.status === "pending" && (
                      <button onClick={() => emailPaymentDetails(v)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#FFF5F7] text-[#FF2768] text-xs font-semibold hover:bg-[#FF2768] hover:text-white" title="Manda email con coordinate pagamento" data-testid={`voucher-email-${v.code}`}>
                        <Mail size={12} /> Coordinate
                      </button>
                    )}
                    {v.status === "pending" && (
                      <button onClick={() => changeStatus(v, "paid")} className="px-3 py-2 rounded-full bg-[#10B981] text-white text-xs font-semibold hover:bg-[#0e9971]" data-testid={`voucher-mark-paid-${v.code}`}>
                        ✓ Segna pagato
                      </button>
                    )}
                    {v.status === "paid" && (
                      <button onClick={() => changeStatus(v, "redeemed")} className="px-3 py-2 rounded-full bg-[#4E9EA5] text-white text-xs font-semibold hover:bg-[#3d7f85]" data-testid={`voucher-mark-redeemed-${v.code}`}>
                        ✓ Riscattato
                      </button>
                    )}
                    {v.status !== "cancelled" && v.status !== "redeemed" && (
                      <button onClick={() => changeStatus(v, "cancelled")} className="px-3 py-2 rounded-full bg-white border border-[#3D2140]/15 text-[#3D2140] text-xs font-semibold hover:border-red-500 hover:text-red-500" data-testid={`voucher-cancel-${v.code}`}>
                        Annulla
                      </button>
                    )}
                    <button onClick={() => del(v)} className="p-2 rounded-full bg-[#FAF8F5] hover:bg-red-500 hover:text-white" title="Elimina" data-testid={`voucher-del-${v.code}`}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
